'use client'

import {
  Disclosure as Collapsible,
  DisclosureButton as CollapsibleButton,
  DisclosurePanel as CollapsiblePanel,
} from '@headlessui/react'
import clsx from 'clsx'
import { PropsWithChildren, ReactNode, useRef } from 'react'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'

export interface CollapsibleSectionProps extends PropsWithChildren {
  /**
   * Displays the title on the collapsible button.
   */
  title: string
  /**
   * Provides additional class-names for the section element.
   */
  sectionClassName?: string
  /**
   * Whether the provided sectionClassName should replace the section's classnames entirely
   */
  overrideSectionClassName?: boolean
  /**
   * Provides additional class-names for the button element.
   */
  buttonClassName?: string
  /**
   * Whether the provided buttonClassName should replace the button's classnames entirely
   */
  overrideButtonClassName?: boolean
  /**
   * Provides additional class-names for the icon in the button element.
   */
  buttonIconClassName?: string
  /**
   * Whether the provided buttonIconClassName should replace the button icon's classnames entirely
   */
  overrideButtonIconClassName?: boolean
  /**
   * Provides additional class-names for the text in the button element.
   */
  buttonTextClassName?: string
  /**
   * Whether the provided buttonTextClassName should replace the button text's classnames entirely
   */
  overrideButtonTextClassName?: boolean
  /**
   * Provides additional class-names for the panel element.
   */
  panelClassName?: string
  /**
   * Whether the provided panelClassName should replace the panel's classnames entirely
   */
  overridePanelClassName?: boolean
  /**
   * Whether the collapsible section should be open by default
   */
  defaultOpen?: boolean
  /**
   * Optional CTA link
   */
  link?: {
    url: string
    content: ReactNode | string
  }
}

export default function CollapsibleSection({
  title,
  children,
  sectionClassName = '',
  overrideSectionClassName = false,
  buttonClassName = '',
  overrideButtonClassName = false,
  buttonIconClassName = '',
  overrideButtonIconClassName = false,
  buttonTextClassName = '',
  overrideButtonTextClassName = false,
  panelClassName = '',
  overridePanelClassName = false,
  defaultOpen = false,
  link,
}: CollapsibleSectionProps) {
  const collapsibleButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <Collapsible
      as="div"
      defaultOpen={defaultOpen}
      className={clsx(
        sectionClassName,
        !overrideSectionClassName &&
          'relative bg-white text-black border border-neutral-600 rounded-8 w-full',
      )}
    >
      {({ open }) => (
        <>
          <div className="flex justify-between items-center">
            <CollapsibleButton
              ref={collapsibleButtonRef}
              className={clsx(
                buttonClassName,
                !overrideButtonClassName &&
                  'p-4 flex w-full gap-2 items-center justify-between type-body-6',
              )}
            >
              <span
                className={clsx(
                  buttonTextClassName,
                  !overrideButtonTextClassName && 'text-neutral-700',
                )}
              >
                {title}
              </span>
              <Icon
                icon={open ? 'minus' : 'plus'}
                className={clsx(
                  buttonIconClassName,
                  !overrideButtonIconClassName && 'w-[24px] h-[24px]',
                )}
              />
            </CollapsibleButton>
            {link?.url ? (
              <Link
                className="md-max:hidden flex-shrink-0 !text-xs mr-8 data-[focus]:outline-accent-link"
                href={link.url}
              >
                {link.content}
              </Link>
            ) : null}
          </div>
          <CollapsiblePanel
            aria-labelledby={collapsibleButtonRef.current?.id}
            className={clsx(
              panelClassName,
              !overridePanelClassName && 'p-4 pt-0',
            )}
            unmount={false}
          >
            {children}
            {link?.url ? (
              <Link
                className="lg:!hidden !text-xs data-[focus]:outline-accent-link"
                href={link.url}
              >
                {link.content}
              </Link>
            ) : null}
          </CollapsiblePanel>
        </>
      )}
    </Collapsible>
  )
}
